import { AccountResponse } from "../../models/ResponseModels/AccountResponse";

export type SubscriptionAccessState = "active" | "warning" | "expired";

const SUBSCRIPTION_WARNING_DAYS = 3;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseSubscriptionExpiry = (value?: string | null): Date | null => {
  if (!value) {
    return null;
  }

  const normalizedValue = value.includes("T") ? value : `${value}T23:59:59`;

  const expiryDate = new Date(normalizedValue);

  if (Number.isNaN(expiryDate.getTime())) {
    return null;
  }

  return expiryDate;
};

export const isCustomSubscription = (
  account?: AccountResponse | null,
): boolean => {
  return account?.subscription_plan === "custom";
};

export const getSubscriptionExpiryValue = (
  account?: AccountResponse | null,
): string | null => {
  return (
    account?.subscription_valid_until ||
    account?.subscription_ends_at ||
    account?.trial_ends_at ||
    null
  );
};

export const getSubscriptionExpiryDate = (
  account?: AccountResponse | null,
): Date | null => {
  return parseSubscriptionExpiry(getSubscriptionExpiryValue(account));
};

export const getSubscriptionDaysLeft = (
  account?: AccountResponse | null,
): number | null => {
  const expiryDate = getSubscriptionExpiryDate(account);

  if (!expiryDate) {
    return null;
  }

  const difference = expiryDate.getTime() - Date.now();

  if (difference <= 0) {
    return 0;
  }

  return Math.ceil(difference / MS_PER_DAY);
};

export const getSubscriptionAccessState = (
  account?: AccountResponse | null,
): SubscriptionAccessState => {
  if (!account) {
    return "active";
  }

  const normalizedStatus = account.subscription_status?.toLowerCase() || "";

  const expiryDate = getSubscriptionExpiryDate(account);

  const isExplicitlyInactive = ["expired", "cancelled"].includes(
    normalizedStatus,
  );

  const isPastExpiry = expiryDate ? expiryDate.getTime() <= Date.now() : false;

  if (
    account.has_active_access === false ||
    isExplicitlyInactive ||
    isPastExpiry
  ) {
    return "expired";
  }

  const daysLeft = getSubscriptionDaysLeft(account);

  if (daysLeft !== null && daysLeft <= SUBSCRIPTION_WARNING_DAYS) {
    return "warning";
  }

  return "active";
};
